import { ChangeEvent, Dispatch, DragEvent, SetStateAction } from 'react';
import { Box, Typography } from '@mui/material';
import { CloudUpload } from 'lucide-react';

import { Button } from '@/components/ui/button';

export interface FileUploadProps {
  accept: string;
  handleChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  handleDrop?: (event: DragEvent<HTMLElement>) => void;
  isDragging: boolean;
  setIsDragging: Dispatch<SetStateAction<boolean>>;
  disabled: boolean;
  orientation?: string;
}

export default function CampaignAssetUploader({
  accept = '.png, .jpg, .jpeg',
  handleChange,
  handleDrop,
  isDragging,
  setIsDragging,
  disabled,
  orientation,
}: FileUploadProps) {
  return (
    <>
      <input
        accept={accept}
        className="hidden"
        data-testid="file-upload"
        disabled={disabled}
        id="file-upload"
        onChange={handleChange}
        type="file"
      />

      <Box
        data-testid="file-uploader-input"
        component={'label'}
        htmlFor="file-upload"
        onDragOver={disabled ? undefined : (e) => e.preventDefault()}
        onDrop={disabled ? undefined : handleDrop}
        onDragEnter={() => !disabled && setIsDragging(true)}
        onDragLeave={() => !disabled && setIsDragging(false)}
        gap="10px"
        sx={{
          cursor: disabled ? 'default' : 'pointer',
          textAlign: 'center',
          display: 'flex',
          border: 'none',
          backgroundImage: isDragging
            ? "url(\"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%23109AB5FF' stroke-width='4' stroke-dasharray='6%2c 8.3' stroke-dashoffset='1' stroke-linecap='round'/%3e%3c/svg%3e\")"
            : "url(\"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%23BFBFC9FF' stroke-width='3' stroke-dasharray='6%2c 8.3' stroke-dashoffset='1' stroke-linecap='round'/%3e%3c/svg%3e\")",
          backgroundColor: isDragging ? 'secondary.contrastText' : 'inherit',
          borderRadius: '8px',
          height: '400px',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '12px',
          '&:hover': {
            backgroundColor: !disabled ? 'secondary.contrastText' : 'inherit',
            color: !disabled ? 'text.secondary' : 'inherit',
            textDecoration: 'none',
          },
        }}
      >
        <Box sx={{ pointerEvents: 'none' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'relative',
              width: '100%',
              gap: '12px',
            }}
          >
            {isDragging ? (
              <CloudUpload className="size-6" />
            ) : (
              <Box display="flex" flexDirection="column" gap="4px">
                {orientation && !disabled && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '18px',
                      opacity: disabled ? 0.5 : 1,
                    }}
                  >
                    {`${
                      orientation === '16:9' ? 'Landscape' : 'Portrait'
                    } (${orientation})`}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '18px',
                    opacity: 0.75,
                  }}
                >
                  {disabled
                    ? 'Select at least one zone to upload a file'
                    : 'Choose a file or drag and drop it here'}
                </Typography>
              </Box>
            )}

            {isDragging ? (
              <Typography variant="body2" color="text.secondary">
                Drop your file here
              </Typography>
            ) : (
              <Button disabled={disabled} variant="outline">
                Upload File
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}
